/**
 * Reports vertical-scroll edge state for a scrollable node, so a caller can
 * show a "more below"/"more above" affordance (e.g. a gradient shadow) only
 * while content actually extends past that edge. Mirrors horizontalScrollHint
 * but for the vertical axis.
 */
export interface VerticalScrollState {
	canScroll: boolean;
	atTop: boolean;
	atBottom: boolean;
}

interface VerticalScrollHintOptions {
	onChange?: (state: VerticalScrollState) => void;
	/** Reports the node's natural content height (scrollHeight plus border-box
	 * chrome) whenever it changes — i.e. the height the node would need to show
	 * everything without scrolling. */
	onContentHeightChange?: (height: number) => void;
}

export const initialVerticalScrollState: VerticalScrollState = {
	canScroll: false,
	atTop: true,
	atBottom: true
};

export const verticalScrollHint = (
	node: HTMLElement,
	initialOptions: VerticalScrollHintOptions = {}
) => {
	let options = initialOptions;
	let previousState = initialVerticalScrollState;
	let previousContentHeight: number | undefined;

	const update = () => {
		const overflow = node.scrollHeight - node.clientHeight;
		const canScroll = overflow > 2;
		const atTop = node.scrollTop <= 2;
		const atBottom = !canScroll || node.scrollTop >= overflow - 2;

		const contentHeight = node.scrollHeight + node.offsetHeight - node.clientHeight;
		if (contentHeight !== previousContentHeight) {
			previousContentHeight = contentHeight;
			options.onContentHeightChange?.(contentHeight);
		}

		node.dataset.scrollHint = canScroll ? 'true' : 'false';
		node.dataset.scrollTop = atTop ? 'true' : 'false';
		node.dataset.scrollBottom = atBottom ? 'true' : 'false';

		const nextState = { canScroll, atTop, atBottom };
		if (
			nextState.canScroll !== previousState.canScroll ||
			nextState.atTop !== previousState.atTop ||
			nextState.atBottom !== previousState.atBottom
		) {
			previousState = nextState;
			options.onChange?.(nextState);
		}
	};

	// The drawer body's content height changes as accordion sections open/close,
	// so observe both the node and its subtree for size changes, not just scroll.
	const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
	resizeObserver?.observe(node);
	Array.from(node.children).forEach((child) => resizeObserver?.observe(child));

	node.addEventListener('scroll', update, { passive: true });
	requestAnimationFrame(update);

	return {
		update(nextOptions: VerticalScrollHintOptions = {}) {
			options = nextOptions;
			update();
		},
		destroy() {
			resizeObserver?.disconnect();
			node.removeEventListener('scroll', update);
		}
	};
};
